import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FaBuilding, 
  FaBookOpen, 
  FaLock, 
  FaUnlock, 
  FaCheckCircle, 
  FaClock, 
  FaFileAlt, 
  FaQuestionCircle, 
  FaChevronDown, 
  FaChevronUp,
  FaMicrophone,
  FaLightbulb,
  FaArrowRight,
  FaShieldAlt,
  FaCalculator,
  FaTasks,
  FaGraduationCap,
  FaStar,
  FaFilter,
  FaAward,
  FaUserCheck,
  FaRegCheckCircle,
  FaHeadset,
  FaLaptop,
  FaKeyboard
} from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const CognizantHub = () => {
    const navigate = useNavigate();
    const [isPremium, setIsPremium] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const [activeCategory, setActiveCategory] = useState('all');
    
    // Interactive Score Diagnostic State for Cognizant Candidates
    const [targetScore, setTargetScore] = useState(58);

    // 20 Mock Tests tailored for Cognizant candidate screening
    const tests = Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        title: `Cognizant Versant Practice Set ${i + 1}`,
        category: i % 3 === 0 ? 'genc' : i % 3 === 1 ? 'voice' : 'chat',
        targetRole: i % 3 === 0 ? 'Cognizant GenC / IT Associate' : i % 3 === 1 ? 'CTS Voice & Support' : 'Cognizant Non-Voice Chat',
        description: `40-question AI simulation matching Cognizant GenC and CTS voice/written screening standards across 9 essential modules.`,
        duration: '35 mins',
        questions: 40,
        difficulty: i < 5 ? 'Easy' : i < 15 ? 'Medium' : 'Hard'
    }));

    useEffect(() => {
        const checkAccess = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const docRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && (docSnap.data().paidTests || docSnap.data().hasPaid)) {
                    setIsPremium(true);
                } else {
                    setIsPremium(false);
                }
            } else {
                setIsPremium(false);
            }
        };
        checkAccess();
    }, []);

    const handleStartTest = (testId) => {
        if (!isPremium && testId > 1) {
            navigate('/pricing');
            return;
        }
        navigate('/');
    };

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const filteredTests = tests.filter(test => {
        if (activeCategory === 'all') return true;
        return test.category === activeCategory;
    });

    const faqs = [
        {
            q: "What is the passing score for Cognizant Versant test?",
            a: "The passing cutoff score for the Cognizant Versant assessment ranges between 52 to 62 points out of 80 (equivalent to CEFR B1+ to B2 level). For Cognizant GenC and IT Associate campus hiring, a score of 52 to 57 is usually accepted. For CTS voice support and international customer operations, a higher threshold of 58 to 65+ points is required."
        },
        {
            q: "What type of questions are asked in Cognizant Versant test?",
            a: "The Cognizant Versant assessment consists of 9 timed modules covering spoken and written communication. Spoken modules include Reading Aloud, Repeat Sentences, Sentence Builds, and Story Retelling. Written modules feature Sentence Completion, Dictation & Spelling, Passage Reconstruction, and a 100+ word workplace Email Response."
        },
        {
            q: "Is Versant test compulsory for Cognizant GenC?",
            a: "Yes, the Versant automated language assessment is a mandatory elimination round in Cognizant's GenC, GenC Next, and CTS recruitment drives. Candidates must clear the cutoff threshold in speech fluency, pronunciation, listening recall, and grammar syntax to qualify for technical and HR interview rounds."
        },
        {
            q: "How can I clear Cognizant Versant assessment?",
            a: "To clear the Cognizant Versant assessment, maintain a steady, natural speaking cadence without long hesitations or artificial fillers, practice 3-4 word phrase chunking for repeat sentences and dictation, and practice typing structured customer emails under a 9-minute timer. Taking full-length AI mock tests beforehand significantly boosts your pass rate."
        }
    ];

    return (
        <div className="bg-slate-50 min-h-screen text-slate-900 font-sans selection:bg-sky-200">
            <Helmet>
                <title>Cognizant Versant Test 2026 | Passing Score & Free Practice</title>
                <meta name="description" content="Preparing for the Cognizant Versant assessment? Learn the GenC cutoff score, test pattern, and practice with free AI-scored mock tests for 2026 recruitment." />
                <link rel="canonical" href="https://versantpro.com/versant-test-for-cognizant" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebPage",
                                "@id": "https://versantpro.com/versant-test-for-cognizant/#webpage",
                                "url": "https://versantpro.com/versant-test-for-cognizant",
                                "name": "Cognizant Versant Test 2026 | Passing Score & Free Practice",
                                "description": "Preparing for the Cognizant Versant assessment? Learn the GenC cutoff score, test pattern, and practice with free AI-scored mock tests for 2026 recruitment."
                            },
                            {
                                "@type": "EducationalApplication",
                                "@id": "https://versantpro.com/versant-test-for-cognizant/#application",
                                "name": "Cognizant Versant Test AI Practice Simulator",
                                "operatingSystem": "All",
                                "applicationCategory": "EducationalApplication",
                                "offers": {
                                    "@type": "Offer",
                                    "price": "0.00",
                                    "priceCurrency": "USD"
                                }
                            },
                            {
                                "@type": "FAQPage",
                                "@id": "https://versantpro.com/versant-test-for-cognizant/#faq",
                                "mainEntity": faqs.map(item => ({
                                    "@type": "Question",
                                    "name": item.q,
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": item.a
                                    }
                                }))
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <Header page="cognizantHub" />

            {/* STICKY QUICK NAVIGATION BAR WITH COGNIZANT CYAN HIGHLIGHTS */}
            <div className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur-md text-white border-b border-sky-500/20 py-2.5 px-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between text-xs font-semibold overflow-x-auto whitespace-nowrap gap-6 no-scrollbar">
                    <div className="flex items-center gap-2 text-sky-400 font-bold shrink-0">
                        <FaBuilding className="text-sky-400" /> Cognizant Prep Portal
                    </div>
                    <div className="flex items-center gap-6">
                        <a href="#overview" className="hover:text-sky-300 transition-colors">Overview</a>
                        <a href="#cognizant-matrix" className="hover:text-sky-300 transition-colors">Passing Scores</a>
                        <a href="#pipeline" className="hover:text-sky-300 transition-colors">Selection Process</a>
                        <a href="#modules" className="hover:text-sky-300 transition-colors">Exam Pattern</a>
                        <a href="#cognizant-tests-grid" className="hover:text-sky-300 transition-colors font-bold text-amber-300">20 Practice Sets</a>
                        <a href="#guide" className="hover:text-sky-300 transition-colors">Prep Strategy</a>
                        <a href="#faqs" className="hover:text-sky-300 transition-colors">FAQs</a>
                    </div>
                    <Link 
                        to="/"
                        className="bg-sky-500 hover:bg-sky-400 text-slate-950 px-3.5 py-1.5 rounded-lg font-black shrink-0 transition-transform active:scale-95 shadow-md"
                    >
                        Free Test ➔
                    </Link>
                </div>
            </div>

            {/* HERO SECTION WITH COGNIZANT DEEP BLUE / CYAN ACCENTS */}
            <section id="overview" className="relative w-full pt-12 pb-20 px-6 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white border-b border-sky-500/15">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-sky-500/15 rounded-full filter blur-3xl opacity-40 animate-blob"></div>

                <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-12 gap-10 items-center">
                    
                    {/* Left Column: Headline & Value Prop (7 cols) */}
                    <div className="lg:col-span-7 text-left space-y-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-sky-300 text-xs sm:text-sm font-bold border border-sky-400/30 shadow-md">
                            <span className="w-2.5 h-2.5 bg-sky-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(56,189,248,0.8)]"></span>
                            Cognizant GenC & CTS Recruitment Guide (2026 Edition)
                        </div>

                        <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight drop-shadow-md">
                            Cognizant Versant Test — <span className="text-sky-400">GenC Passing Score</span>, Pattern & Free Practice
                        </h1>

                        <p className="text-base sm:text-lg text-slate-300 font-light leading-relaxed">
                            Complete preparation guide for Cognizant GenC, GenC Next, CTS Voice, and IT Associate screening. Practice with full-length AI mock tests matching Cognizant's automated scoring rules.
                        </p>

                        <div className="grid grid-cols-3 gap-3 pt-2 text-center text-xs">
                            <div className="bg-white/5 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-md">
                                <div className="text-amber-300 font-black text-lg">52–58+</div>
                                <div className="text-slate-300 text-[11px] font-medium">GenC Cutoff Score</div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-md">
                                <div className="text-sky-300 font-black text-lg">Spoken & Written</div>
                                <div className="text-slate-300 text-[11px] font-medium">9 Timed Modules</div>
                            </div>
                            <div className="bg-white/5 backdrop-blur-md p-3 rounded-2xl border border-white/10 shadow-md">
                                <div className="text-teal-300 font-black text-lg">Instant AI</div>
                                <div className="text-slate-300 text-[11px] font-medium">GSE & CEFR Feedback</div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-4 pt-2">
                            <Link 
                                to="/"
                                className="px-8 py-4 bg-gradient-to-r from-sky-400 to-blue-500 text-slate-950 font-black text-sm rounded-2xl shadow-lg hover:scale-105 transition-all flex items-center gap-2"
                            >
                                Start Free Cognizant Practice Test <FaArrowRight />
                            </Link>
                            <a 
                                href="#cognizant-matrix"
                                className="px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-2xl border border-white/20 transition-colors"
                            >
                                View Passing Score Table 👇
                            </a>
                        </div>
                    </div>

                    {/* Right Column: Interactive Cognizant Score Diagnostic Calculator Widget (5 cols) */}
                    <div className="lg:col-span-5 bg-white/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl space-y-6">
                        <div className="flex items-center justify-between border-b border-white/10 pb-4">
                            <div className="flex items-center gap-2 text-sky-300 font-bold text-sm">
                                <FaCalculator /> Cognizant Score Diagnostic Calculator
                            </div>
                            <span className="text-[10px] uppercase bg-sky-400 text-slate-950 font-black px-2.5 py-0.5 rounded-full">
                                Interactive
                            </span>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-slate-200 flex justify-between">
                                <span>Target Cognizant Assessment Score:</span>
                                <span className="text-amber-300 font-black text-sm">{targetScore} / 80</span>
                            </label>
                            <input 
                                type="range" 
                                min="45" 
                                max="75" 
                                value={targetScore} 
                                onChange={(e) => setTargetScore(Number(e.target.value))}
                                className="w-full accent-sky-400 cursor-pointer"
                            />
                        </div>

                        <div className="bg-slate-950/70 rounded-2xl p-4 border border-white/10 space-y-3 text-xs">
                            <div className="flex justify-between items-center text-slate-300">
                                <span>CEFR Alignment:</span>
                                <span className="font-extrabold text-sky-300 bg-sky-950/60 px-2.5 py-0.5 rounded border border-sky-500/30">
                                    {targetScore >= 65 ? 'C1 (Advanced Fluent)' : targetScore >= 56 ? 'B2 (Upper Intermediate)' : 'B1 (Intermediate)'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-slate-300">
                                <span>Cognizant Profile Qualification:</span>
                                <span className="font-extrabold text-amber-300 bg-amber-950/60 px-2.5 py-0.5 rounded border border-amber-500/30">
                                    {targetScore >= 60 ? 'CTS Voice & Customer Support' : targetScore >= 52 ? 'Cognizant GenC / IT Associate' : 'Non-Voice Chat'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-slate-300">
                                <span>Recommended Practice:</span>
                                <span className="font-bold text-white">15 – 20 Full Mock Tests</span>
                            </div>
                        </div>

                        <Link 
                            to="/"
                            className="w-full py-3.5 bg-sky-400 hover:bg-sky-300 text-slate-950 font-extrabold text-xs rounded-xl flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-95"
                        >
                            Test Your Current Score Now ➔
                        </Link>
                    </div>

                </div>
            </section>

            {/* SELECTION PIPELINE WORKFLOW (4-Step Cards) */}
            <section id="pipeline" className="max-w-7xl mx-auto px-6 py-16 -mt-8 relative z-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="text-center max-w-2xl mx-auto space-y-2">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-sky-50 text-sky-800 rounded-full text-xs font-bold uppercase tracking-widest border border-sky-200/80">
                            <FaTasks /> Hiring Roadmap
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Cognizant Recruitment Pipeline: Where Versant Fits
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-500">Understanding Cognizant's GenC & CTS 4-stage recruitment workflow.</p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4 relative">
                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 relative group hover:border-sky-400 transition-all">
                            <div className="w-8 h-8 rounded-xl bg-slate-900 text-sky-400 font-black flex items-center justify-center text-xs mb-3 shadow-md">
                                01
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 mb-1">Aptitude & Technical</h3>
                            <p className="text-[11px] text-slate-600 font-light leading-relaxed">
                                Initial cognitive screening evaluating quantitative reasoning, logical ability, and basic coding/technical fundamentals.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-sky-50 via-white to-sky-50/40 rounded-2xl p-5 border-2 border-sky-400 relative group shadow-md scale-105 z-10">
                            <div className="absolute -top-2.5 right-3 px-2 py-0.5 bg-sky-500 text-slate-950 text-[9px] font-black uppercase rounded-full shadow-sm">
                                ELIMINATION ROUND
                            </div>
                            <div className="w-8 h-8 rounded-xl bg-sky-500 text-slate-950 font-black flex items-center justify-center text-xs mb-3 shadow-md">
                                02
                            </div>
                            <h3 className="text-sm font-extrabold text-slate-900 mb-1">Cognizant Versant AI</h3>
                            <p className="text-[11px] text-slate-700 font-normal leading-relaxed">
                                Automated speech recognition and written evaluation testing pronunciation, fluency, dictation, and email composition.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 relative group hover:border-sky-400 transition-all">
                            <div className="w-8 h-8 rounded-xl bg-slate-900 text-sky-400 font-black flex items-center justify-center text-xs mb-3 shadow-md">
                                03
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 mb-1">Technical Interview</h3>
                            <p className="text-[11px] text-slate-600 font-light leading-relaxed">
                                Technical round with Cognizant interviewers covering programming concepts, domain projects, and problem-solving skills.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 relative group hover:border-sky-400 transition-all">
                            <div className="w-8 h-8 rounded-xl bg-slate-900 text-sky-400 font-black flex items-center justify-center text-xs mb-3 shadow-md">
                                04
                            </div>
                            <h3 className="text-sm font-bold text-slate-900 mb-1">HR & Offer Letter</h3>
                            <p className="text-[11px] text-slate-600 font-light leading-relaxed">
                                Final HR discussion regarding work location preferences, shift flexibility, background verification, and formal job offer.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* COGNIZANT SCORE MATRIX TABLE */}
            <section id="cognizant-matrix" className="max-w-7xl mx-auto px-6 py-4">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-6">
                    <div className="border-b border-slate-100 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 text-sky-800 font-extrabold text-xs uppercase tracking-widest bg-sky-50 px-3 py-1 rounded-full mb-2 border border-sky-200/80">
                                <FaShieldAlt /> Cutoff Matrix
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                                Cognizant Versant Score Requirements by Profile
                            </h2>
                        </div>
                        <span className="text-xs text-slate-500 font-medium bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 self-start sm:self-auto">
                            Updated for 2026 Hiring Drives
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[650px]">
                            <thead>
                                <tr className="bg-slate-900 text-white text-xs font-black uppercase tracking-wider">
                                    <th className="p-4 rounded-l-xl">Cognizant Profile</th>
                                    <th className="p-4">Passing Score</th>
                                    <th className="p-4">CEFR Level</th>
                                    <th className="p-4">Key Tested Skills</th>
                                    <th className="p-4 rounded-r-xl">Priority Modules</th>
                                </tr>
                            </thead>
                            <tbody className="text-xs text-slate-600 divide-y divide-slate-100">
                                <tr className="hover:bg-sky-50/40 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 bg-sky-500 rounded-full"></span>
                                        Cognizant GenC / IT Associate
                                    </td>
                                    <td className="p-4 font-black text-sky-600 text-sm">52 – 57 / 80</td>
                                    <td className="p-4 font-semibold">CEFR B1+ / B2</td>
                                    <td className="p-4">Listening Comprehension & Sentence Mastery</td>
                                    <td className="p-4 font-medium text-slate-800">Sentence Completion & Builds</td>
                                </tr>
                                <tr className="hover:bg-sky-50/40 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 bg-blue-600 rounded-full"></span>
                                        CTS Voice & International Support
                                    </td>
                                    <td className="p-4 font-black text-sky-600 text-sm">58 – 65+ / 80</td>
                                    <td className="p-4 font-semibold">CEFR B2+ / C1</td>
                                    <td className="p-4">Fluency, Accent Clarity & Active Listening</td>
                                    <td className="p-4 font-medium text-slate-800">Repeat Sentences & Story Retelling</td>
                                </tr>
                                <tr className="hover:bg-sky-50/40 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 bg-teal-500 rounded-full"></span>
                                        Cognizant Non-Voice Chat Support
                                    </td>
                                    <td className="p-4 font-black text-sky-600 text-sm">55 – 60 / 80</td>
                                    <td className="p-4 font-semibold">CEFR B2</td>
                                    <td className="p-4">Grammar, Typing Precision & Email Composition</td>
                                    <td className="p-4 font-medium text-slate-800">Passage Reconstruction & Email Writing</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* EXAM FORMAT SECTION */}
            <section id="modules" className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="space-y-2">
                        <div className="inline-flex items-center gap-2 text-sky-800 font-extrabold text-xs uppercase tracking-widest bg-sky-50 px-3 py-1 rounded-full border border-sky-200/80">
                            <FaBookOpen /> Comprehensive Exam Pattern
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Cognizant Versant Test Pattern & Module Breakdown
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-normal max-w-4xl">
                            The Cognizant Versant assessment combines automated audio speech analysis with computer-evaluated written syntax drills across 9 timed sub-sections:
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-slate-50 via-sky-50/20 to-white rounded-2xl p-6 border border-sky-200/80 space-y-5">
                            <div className="flex items-center justify-between">
                                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                                    <FaMicrophone className="text-sky-600" /> Audio Spoken Modules
                                </h3>
                                <span className="bg-sky-100 text-sky-900 text-[10px] font-black px-2.5 py-1 rounded-full uppercase border border-sky-200">
                                    Speech AI Evaluated
                                </span>
                            </div>
                            <ul className="text-xs sm:text-sm text-slate-600 space-y-3 list-none font-light">
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-sky-500 shrink-0 mt-0.5" />
                                    <div><strong>Read Aloud:</strong> Read sentences on screen with natural cadence and smooth pronunciation.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-sky-500 shrink-0 mt-0.5" />
                                    <div><strong>Repeat Sentences:</strong> Listen to 16 spoken sentences once and repeat them verbatim.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-sky-500 shrink-0 mt-0.5" />
                                    <div><strong>Sentence Builds:</strong> Combine 3 jumbled audio phrases into a coherent spoken sentence.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-sky-500 shrink-0 mt-0.5" />
                                    <div><strong>Story Retelling:</strong> Listen to a short 30s scenario and summarize all details in spoken English.</div>
                                </li>
                            </ul>
                            <div className="pt-2">
                                <Link to="/versant-speaking-and-listening-practice-test" className="text-xs font-extrabold text-sky-700 hover:text-sky-800 flex items-center gap-1">
                                    Practice Spoken Audio Modules <FaArrowRight className="text-[10px]" />
                                </Link>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200 space-y-5">
                            <div className="flex items-center justify-between">
                                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                                    <FaKeyboard className="text-slate-700" /> Keyboard Written Modules
                                </h3>
                                <span className="bg-slate-100 text-slate-800 text-[10px] font-black px-2.5 py-1 rounded-full uppercase border border-slate-200">
                                    Typing & Syntax AI
                                </span>
                            </div>
                            <ul className="text-xs sm:text-sm text-slate-600 space-y-3 list-none font-light">
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-slate-500 shrink-0 mt-0.5" />
                                    <div><strong>Sentence Completion:</strong> Fill in vocabulary context gaps in incomplete sentences.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-slate-500 shrink-0 mt-0.5" />
                                    <div><strong>Dictation & Spelling:</strong> Listen to audio sentences and type them out with 100% spelling precision.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-slate-500 shrink-0 mt-0.5" />
                                    <div><strong>Passage Reconstruction:</strong> Read a short paragraph for 30s and reconstruct the main points.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-slate-500 shrink-0 mt-0.5" />
                                    <div><strong>Email Writing:</strong> Write a 100+ word formal workplace business email in 9 minutes.</div>
                                </li>
                            </ul>
                            <div className="pt-2">
                                <Link to="/versant-reading-and-writing-mock-test" className="text-xs font-extrabold text-slate-800 hover:text-slate-950 flex items-center gap-1">
                                    Practice Written Keyboard Modules <FaArrowRight className="text-[10px]" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 20 MOCK TESTS CUSTOM GRID WITH FEATURED CARD & CATEGORY FILTER */}
            <main id="cognizant-tests-grid" className="max-w-7xl mx-auto px-6 py-12">
                
                {/* Header & Filter Controls */}
                <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-xl mb-8 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                        <div>
                            <span className="inline-flex items-center gap-2 text-sky-800 font-extrabold text-xs uppercase tracking-widest bg-sky-50 px-3 py-1 rounded-full mb-1 border border-sky-200/80">
                                <FaAward /> Cognizant Practice Exam Suite
                            </span>
                            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                                Cognizant Versant Practice Sets (1–20)
                            </h2>
                            <p className="text-slate-500 text-xs sm:text-sm mt-1">Select a practice exam below to test under Cognizant AI evaluation standards.</p>
                        </div>

                        {/* Category Filter Pills */}
                        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                            <button 
                                onClick={() => setActiveCategory('all')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'all' 
                                        ? 'bg-slate-900 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                All (20 Sets)
                            </button>
                            <button 
                                onClick={() => setActiveCategory('genc')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'genc' 
                                        ? 'bg-sky-500 text-slate-950 shadow-md font-extrabold' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                GenC & IT Roles
                            </button>
                            <button 
                                onClick={() => setActiveCategory('voice')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'voice' 
                                        ? 'bg-blue-600 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                CTS Voice
                            </button>
                            <button 
                                onClick={() => setActiveCategory('chat')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'chat' 
                                        ? 'bg-slate-800 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                Non-Voice Chat
                            </button>
                        </div>
                    </div>

                    {/* FEATURED TEST #1 BANNER CARD */}
                    <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 text-white rounded-2xl p-6 sm:p-8 relative overflow-hidden border border-sky-400/30 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-3 z-10 max-w-2xl">
                            <div className="flex items-center gap-2 text-xs font-extrabold text-sky-400">
                                <FaStar /> FEATURED FREE DIAGNOSTIC MOCK TEST
                            </div>
                            <h3 className="text-xl sm:text-2xl font-black">Cognizant Versant Practice Set 1 (100% Free)</h3>
                            <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed">
                                Full-length 40-question AI simulation matching Cognizant GenC and CTS screening formats. Evaluates spoken fluency, dictation, grammar, and written email composition.
                            </p>
                            <div className="flex items-center gap-4 text-xs text-sky-200 pt-1">
                                <span className="flex items-center gap-1 font-semibold"><FaClock /> 35 Mins</span>
                                <span className="flex items-center gap-1 font-semibold"><FaFileAlt /> 40 Questions</span>
                                <span className="bg-sky-400/20 px-2.5 py-0.5 rounded-full border border-sky-400/40 text-sky-300 font-bold">Free Unlocked</span>
                            </div>
                        </div>
                        <button 
                            onClick={() => handleStartTest(1)}
                            className="z-10 px-8 py-4 bg-sky-400 hover:bg-sky-300 text-slate-950 font-black text-sm rounded-2xl shadow-lg transition-all hover:scale-105 active:scale-95 shrink-0"
                        >
                            Start Cognizant Test 1 Now ➔
                        </button>
                    </div>
                </div>

                {/* Grid of Remaining Tests */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTests.map((test) => {
                        const isLocked = !isPremium && test.id > 1;

                        return (
                            <div
                                key={test.id}
                                className={`relative group bg-white rounded-3xl p-6 border-2 transition-all duration-300 flex flex-col justify-between ${
                                    isLocked
                                      ? 'border-slate-200/80 bg-slate-50/50 opacity-90 hover:border-slate-300'
                                      : 'border-sky-100 hover:border-sky-400 shadow-md hover:shadow-2xl hover:-translate-y-1'
                                }`}
                            >
                                <div>
                                    <div className="flex items-center justify-between gap-2 mb-4">
                                        <span className="text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                            {test.targetRole}
                                        </span>
                                        {isLocked ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                                                <FaLock className="text-amber-600 text-[10px]" /> Pro Only
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                                                <FaUnlock className="text-emerald-600 text-[10px]" /> Unlocked
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="text-lg font-extrabold text-slate-900 group-hover:text-sky-600 transition-colors mb-2">
                                        {test.title}
                                    </h3>

                                    <p className="text-xs text-slate-600 font-light leading-relaxed mb-6">
                                        {test.description}
                                    </p>
                                </div>

                                <div className="space-y-4 pt-4 border-t border-slate-100">
                                    <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                                        <span className="flex items-center gap-1"><FaClock className="text-sky-600" /> {test.duration}</span>
                                        <span className="flex items-center gap-1"><FaFileAlt className="text-sky-600" /> {test.questions} Qs</span>
                                        <span className={`font-bold ${test.difficulty === 'Easy' ? 'text-emerald-600' : test.difficulty === 'Medium' ? 'text-amber-600' : 'text-rose-600'}`}>
                                            {test.difficulty}
                                        </span>
                                    </div>

                                    <button
                                        onClick={() => handleStartTest(test.id)}
                                        className={`w-full py-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${
                                            isLocked
                                                ? 'bg-slate-100 hover:bg-sky-400 hover:text-slate-950 text-slate-600 border border-slate-200 font-bold'
                                                : 'bg-sky-500 hover:bg-sky-400 text-slate-950 font-black shadow-md hover:shadow-lg'
                                        }`}
                                    >
                                        {isLocked ? (
                                            <>
                                                <FaLock className="text-[11px]" /> Unlock Pro Test Set
                                            </>
                                        ) : (
                                            <>
                                                Start Practice Exam ➔
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </main>

            {/* 800-1000 WORD RICH EDUCATIONAL GUIDE SECTION */}
            <section id="guide" className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-10">
                    
                    {/* Section 1: Intro */}
                    <div className="space-y-4">
                        <div className="inline-flex items-center gap-2 text-sky-800 font-extrabold text-xs uppercase tracking-widest bg-sky-50 px-3 py-1 rounded-full border border-sky-200/80">
                            <FaBuilding /> Cognizant Candidate Screening Guide
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Understanding the Cognizant Versant Test Evaluation
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            Cognizant Technology Solutions (CTS), a global leader in IT services, consulting, and business process outsourcing, uses the <strong>Cognizant Versant Test</strong> as an automated screening tool during candidate selection. Whether you are appearing for <strong>Cognizant GenC</strong> campus placement drives, <strong>GenC Next</strong> technical roles, or <strong>CTS Voice & BPO</strong> customer operations, demonstrating fluent English communication is mandatory.
                        </p>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            Because Cognizant teams support Fortune 500 clients across North America, Europe, and Asia-Pacific, candidate communication skills are evaluated automatically by advanced AI speech recognition engines. The assessment scores your spoken pronunciation clarity, cadence rhythm, active listening recall, and written email composition under strict time constraints.
                        </p>
                    </div>

                    {/* Section 2: Score Requirements */}
                    <div className="space-y-4 pt-6 border-t border-slate-100">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Cognizant Versant Score Requirements & Cutoff Thresholds
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            The Versant test is evaluated on a scale from 20 to 80 points, mapped directly to global language frameworks like the <strong>CEFR (Common European Framework of Reference for Languages)</strong> and <strong>GSE (Global Scale of English)</strong>.
                        </p>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            For <strong>Cognizant GenC and IT Associate campus roles</strong>, the passing cutoff score generally falls between <strong>52 to 57 points</strong> (CEFR B1+ / B2 level). For <strong>CTS Voice Support</strong> and international customer operations, a higher score of <strong>58 to 65+ points</strong> (CEFR B2+ / C1 level) is required to clear the elimination round.
                        </p>
                    </div>

                    {/* Section 3: Preparation Tips */}
                    <div className="space-y-4 pt-6 border-t border-slate-100">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            4 Essential Preparation Tips for Cognizant Candidates
                        </h2>

                        <div className="grid md:grid-cols-2 gap-5 pt-2">
                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-800 text-xs flex items-center justify-center font-bold">1</span>
                                    Maintain Continuous Cadence Without Hesitation
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    The automated AI speech engine penalizes long pauses, self-corrections, and artificial filler sounds like "um" or "ah." Maintain a smooth, steady conversational flow throughout Read Aloud and Repeat Sentences.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-800 text-xs flex items-center justify-center font-bold">2</span>
                                    Use 3–4 Word Phrase Chunking
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    During Repeat Sentences and Dictation, group spoken words into 3-4 word meaningful phrase chunks rather than attempting to memorize individual words.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-800 text-xs flex items-center justify-center font-bold">3</span>
                                    Structure Email Responses Formally
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    Use standard business email formatting: Professional Salutation, Situation Summary, Actionable Resolution Steps, and Formal Sign-off to earn maximum organization points.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-800 text-xs flex items-center justify-center font-bold">4</span>
                                    Practice Timed AI Diagnostic Mock Tests
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    Simulating the exact 35-minute test environment reduces exam anxiety and helps you adjust to strict section timers.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Banner CTA Box */}
                    <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 text-white rounded-2xl p-6 sm:p-8 border border-sky-400/30 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl">
                        <div className="space-y-2 max-w-2xl">
                            <span className="inline-block px-3 py-1 bg-sky-400/20 text-sky-300 text-xs font-bold rounded-full border border-sky-400/40 uppercase tracking-wider">
                                🚀 Ready for Cognizant Recruitment Drives?
                            </span>
                            <h3 className="text-xl font-extrabold">Start Free Cognizant Versant Practice Test</h3>
                            <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed">
                                Take a 100% free full-length AI mock test with instant GSE score report and section-by-section breakdown.
                            </p>
                        </div>
                        <Link 
                          to="/" 
                          className="px-8 py-4 bg-sky-400 hover:bg-sky-300 text-slate-950 font-black text-xs rounded-xl shadow-lg transition-transform active:scale-95 whitespace-nowrap shrink-0"
                        >
                            Start Free Test Now ➔
                        </Link>
                    </div>

                </div>
            </section>

            {/* FREQUENTLY ASKED QUESTIONS SECTION */}
            <section id="faqs" className="max-w-7xl mx-auto px-6 py-8 pb-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="text-center max-w-2xl mx-auto space-y-2">
                        <div className="inline-flex items-center gap-2 text-sky-800 font-extrabold text-xs uppercase tracking-widest bg-sky-50 px-3 py-1 rounded-full border border-sky-200/80">
                            <FaQuestionCircle /> Frequently Asked Questions
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Cognizant Versant Assessment FAQs
                        </h2>
                    </div>

                    <div className="space-y-4 max-w-4xl mx-auto">
                        {faqs.map((faq, index) => (
                            <div 
                                key={index} 
                                className="border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200"
                            >
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full text-left p-5 bg-slate-50 hover:bg-slate-100 flex justify-between items-center gap-4 transition-colors font-bold text-slate-900 text-sm sm:text-base"
                                >
                                    <span>{faq.q}</span>
                                    {openFaq === index ? (
                                        <FaChevronUp className="text-sky-600 shrink-0 text-sm" />
                                    ) : (
                                        <FaChevronDown className="text-slate-400 shrink-0 text-sm" />
                                    )}
                                </button>
                                {openFaq === index && (
                                    <div className="p-5 bg-white text-xs sm:text-sm text-slate-600 font-light leading-relaxed border-t border-slate-100">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default CognizantHub;
